/**
 * Configurateur de Piscine
 * Gestion interactive du formulaire de configuration de piscine
 */

class ConfigurateurPiscine {
  constructor(container) {
    this.container = container;
    this.form = container.querySelector('[data-configurateur-form]');
    this.summaryContent = container.querySelector('[data-summary-content]');
    this.totalPrice = container.querySelector('[data-total-price]');
    this.addToCartBtn = container.querySelector('[data-add-configuration]');
    // Map pour stocker les produits sélectionnés: stepName -> Map(variantId -> productData)
    this.selectedProducts = new Map();
    
    // Récupérer les informations du produit principal depuis les data attributes
    this.mainProduct = {
      id: container.dataset.mainProductId,
      variantId: container.dataset.mainProductVariantId,
      price: parseFloat(container.dataset.mainProductPrice) / 100 || 0, // Shopify stocke en centimes
      priceFormatted: container.dataset.mainProductPriceFormatted,
      title: container.dataset.mainProductTitle,
      image: container.dataset.mainProductImage,
      sku: container.dataset.mainProductSku || ''
    };

    this.depotVariantId = container.dataset.depotVariantId || null;
    
    // Récupérer les traductions pour les messages
    this.i18n = {
      success: container.dataset.i18nSuccess || 'Configuration added to cart successfully!',
      error: container.dataset.i18nError || 'An error occurred while adding to cart.',
      maxQuantity: container.dataset.i18nMaxQuantity || 'The maximum quantity of one or more items is already in your cart.',
      unavailable: container.dataset.i18nUnavailable || 'One or more products are no longer available.',
      networkError: container.dataset.i18nNetworkError || 'Network error. Please check your connection and try again.',
      genericError: container.dataset.i18nGenericError || 'An error occurred. Please try again.'
    };
    
    console.log('🏊 Produit principal (piscine):', this.mainProduct);
    
    // Vérifier que les éléments essentiels existent
    if (!this.form) {
      console.error('Formulaire du configurateur non trouvé');
      return;
    }
    
    this.init();
  }

  init() {
    // Réinitialiser complètement le formulaire au chargement
    this.resetFormCompletely();
    
    this.bindEvents();
    this.updateSummary();
    
    // Ouvrir la première étape par défaut
    const firstStep = this.container.querySelector('.configurateur-step[data-step="1"]');
    if (firstStep) {
      firstStep.setAttribute('data-step-active', 'true');
    }
  }

  bindEvents() {
    // Gestion des étapes accordéon - toute la ligne cliquable
    this.container.addEventListener('click', (e) => {
      const stepHeader = e.target.closest('.configurateur-step__header');
      if (stepHeader) {
        this.toggleStepFromHeader(stepHeader);
        return;
      }

      // Gestion des boutons "Aucune"
      const skipBtn = e.target.closest('[data-skip-step]');
      if (skipBtn) {
        this.handleSkipStep(skipBtn);
      }
    });

    // Gestion des sélections d'options (checkbox pour multi-sélection)
    this.form.addEventListener('change', (e) => {
      if (e.target.type === 'checkbox' || e.target.type === 'radio') {
        this.handleOptionChange(e.target);
      }
    });

    // Ajout au panier
    if (this.addToCartBtn) {
      this.addToCartBtn.addEventListener('click', () => {
        this.addConfigurationToCart();
      });
    }
  }

  toggleStep(toggle) {
    const step = toggle.closest('.configurateur-step');
    const isActive = step.getAttribute('data-step-active') === 'true';
    
    step.setAttribute('data-step-active', !isActive);
    
    // Animation du chevron
    const icon = toggle.querySelector('svg, .icon');
    if (icon) {
      icon.style.transform = isActive ? 'rotate(-90deg)' : 'rotate(0deg)';
    }
  }

  toggleStepFromHeader(stepHeader) {
    const step = stepHeader.closest('.configurateur-step');
    const isActive = step.getAttribute('data-step-active') === 'true';
    
    step.setAttribute('data-step-active', !isActive);
  }

  parseMoneyValue(priceString) {
    if (!priceString) return 0;

    const cleaned = String(priceString).replace(/[^\d.,-]/g, '').trim();
    if (!cleaned) return 0;

    const hasComma = cleaned.includes(',');
    const hasDot = cleaned.includes('.');
    let normalized = cleaned;

    if (hasComma && hasDot) {
      // Use the last separator as decimal marker and treat the other as thousands separator.
      if (cleaned.lastIndexOf(',') > cleaned.lastIndexOf('.')) {
        normalized = cleaned.replace(/\./g, '').replace(',', '.');
      } else {
        normalized = cleaned.replace(/,/g, '');
      }
    } else if (hasComma) {
      const commaIndex = cleaned.lastIndexOf(',');
      const decimalsCount = cleaned.length - commaIndex - 1;
      normalized = decimalsCount === 2 ? cleaned.replace(',', '.') : cleaned.replace(/,/g, '');
    } else if (hasDot) {
      const dots = cleaned.match(/\./g) || [];
      if (dots.length > 1) {
        const lastDotIndex = cleaned.lastIndexOf('.');
        normalized = `${cleaned.slice(0, lastDotIndex).replace(/\./g, '')}${cleaned.slice(lastDotIndex)}`;
      }
    }

    const parsed = Number.parseFloat(normalized);
    return Number.isFinite(parsed) ? parsed : 0;
  }

  handleOptionChange(input) {
    const stepName = input.name;
    const variantId = input.value;
    const productData = {
      id: variantId,
      productId: input.dataset.productId,
      title: input.dataset.productTitle,
      price: input.dataset.productPrice,
      priceValue: this.parseMoneyValue(input.dataset.productPrice),
      image: input.dataset.productImage,
      sku: input.dataset.productSku || ''
    };

    console.log('🔥 Produit modifié:', {
      stepName,
      variantId: input.value,
      productId: input.dataset.productId,
      title: input.dataset.productTitle,
      price: input.dataset.productPrice,
      checked: input.checked,
      allDatasets: input.dataset
    });

    // Initialiser la Map pour cette étape si elle n'existe pas
    if (!this.selectedProducts.has(stepName)) {
      this.selectedProducts.set(stepName, new Map());
    }

    const stepProducts = this.selectedProducts.get(stepName);

    if (input.checked) {
      // Ajouter le produit à la sélection de cette étape
      stepProducts.set(variantId, productData);
    } else {
      // Retirer le produit de la sélection de cette étape
      stepProducts.delete(variantId);
    }

    // Si l'étape n'a plus de produits sélectionnés, la supprimer
    if (stepProducts.size === 0) {
      this.selectedProducts.delete(stepName);
    }
    
    // Mettre à jour l'état de l'étape
    this.updateStepState(input);
    
    this.updateSummary();
  }

  updateStepState(input) {
    const step = input.closest('.configurateur-step');
    const stepName = input.name;
    
    // Vérifier si au moins un produit est sélectionné dans cette étape
    const hasSelection = this.selectedProducts.has(stepName) && this.selectedProducts.get(stepName).size > 0;
    
    if (hasSelection) {
      step.setAttribute('data-step-completed', 'true');
      step.removeAttribute('data-step-skipped');
      
      // Mettre à jour l'aperçu avec les produits sélectionnés
      const stepProducts = this.selectedProducts.get(stepName);
      const productsArray = Array.from(stepProducts.values());
      const firstProduct = productsArray[0];
      const count = productsArray.length;
      
      this.updateStepPreview(step, {
        title: count > 1 ? `${count} articles sélectionnés` : firstProduct.title,
        image: firstProduct.image
      });
    } else {
      step.removeAttribute('data-step-completed');
      step.removeAttribute('data-step-skipped');
      this.resetStepPreview(step);
    }
  }

  handleSkipStep(skipBtn) {
    const stepName = skipBtn.dataset.skipStep;
    
    // Décocher toutes les options de cette étape (checkbox et radio)
    const inputs = this.form.querySelectorAll(`input[name="${stepName}"]`);
    inputs.forEach(input => {
      input.checked = false;
    });
    
    // Supprimer de la sélection
    this.selectedProducts.delete(stepName);
    
    // Marquer l'étape comme remplie (même avec "Aucune")
    this.markStepAsSkipped(skipBtn);
    
    // Mettre à jour l'affichage
    this.updateSummary();
    
    // Fermer l'étape courante et ouvrir la suivante
    const currentStep = skipBtn.closest('.configurateur-step');
    const currentStepNumber = parseInt(currentStep.dataset.step);
    
    // Fermer l'étape actuelle
    setTimeout(() => {
      currentStep.setAttribute('data-step-active', 'false');
    }, 500);
    
    // Ouvrir l'étape suivante
    const nextStep = this.container.querySelector(`.configurateur-step[data-step="${currentStepNumber + 1}"]`);
    if (nextStep) {
      setTimeout(() => {
        nextStep.setAttribute('data-step-active', 'true');
        nextStep.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }, 600);
    }
  }

  openNextStep(currentInput) {
    const currentStep = currentInput.closest('.configurateur-step');
    const currentStepNumber = parseInt(currentStep.dataset.step);
    
    // Fermer l'étape actuelle
    setTimeout(() => {
      currentStep.setAttribute('data-step-active', 'false');
    }, 500);
    
    // Ouvrir l'étape suivante
    const nextStep = this.container.querySelector(`.configurateur-step[data-step="${currentStepNumber + 1}"]`);
    if (nextStep) {
      setTimeout(() => {
        nextStep.setAttribute('data-step-active', 'true');
        nextStep.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }, 600);
    }
  }

  updateSummary() {
    if (!this.summaryContent) {
      console.error('Contenu du résumé non trouvé');
      return;
    }
    
    const summaryEmpty = this.summaryContent.querySelector('.configurateur-summary__empty');
    const mainProductSummary = this.summaryContent.querySelector('[data-main-product-summary]');
    
    // Le produit principal est toujours visible
    if (mainProductSummary) {
      mainProductSummary.style.display = 'flex';
    }
    
    // Masquer le message "aucune option" car le produit principal est toujours présent
    if (summaryEmpty) summaryEmpty.style.display = 'none';
    
    // Vider le contenu précédent (sauf le produit principal)
    this.summaryContent.querySelectorAll('.configurateur-summary__item:not([data-main-product-summary])').forEach(item => item.remove());
    
    // Commencer avec le prix du produit principal (piscine)
    let total = this.mainProduct.price;
    
    // Parcourir chaque étape et ses produits sélectionnés
    this.selectedProducts.forEach((stepProducts, stepName) => {
      // stepProducts est maintenant une Map de produits pour cette étape
      stepProducts.forEach((product, variantId) => {
        total += product.priceValue;
        
        const item = document.createElement('div');
        item.className = 'configurateur-summary__item';
        item.innerHTML = `
          <div class="summary-item__info">
            ${product.image ? `<img src="${product.image}" alt="${product.title}" class="summary-item__image">` : ''}
            <span class="summary-item__title">${product.title}</span>
          </div>
          <span class="summary-item__price">${product.price}</span>
          <button type="button" class="summary-item__remove" data-remove-item="${stepName}" data-variant-id="${variantId}">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
              <path d="M12 4l-8 8m0-8l8 8"/>
            </svg>
          </button>
        `;
        
        // Ajouter l'événement de suppression
        const removeBtn = item.querySelector('[data-remove-item]');
        if (removeBtn) {
          removeBtn.addEventListener('click', () => this.removeItem(stepName, variantId));
        }
        
        this.summaryContent.appendChild(item);
      });
    });
    
    if (this.totalPrice) {
      this.totalPrice.textContent = new Intl.NumberFormat('fr-CA', {
        style: 'currency',
        currency: 'CAD'
      }).format(total);
    }
    
    // Le bouton est toujours actif car le produit principal est toujours inclus
    if (this.addToCartBtn) {
      this.addToCartBtn.disabled = false;
    }
  }

  removeItem(stepName, variantId) {
    // Décocher l'option correspondante
    const input = this.form.querySelector(`input[name="${stepName}"][value="${variantId}"]`);
    if (input) {
      input.checked = false;
    }
    
    // Supprimer le produit de la map
    if (this.selectedProducts.has(stepName)) {
      const stepProducts = this.selectedProducts.get(stepName);
      stepProducts.delete(variantId);
      
      // Si l'étape n'a plus de produits, la supprimer
      if (stepProducts.size === 0) {
        this.selectedProducts.delete(stepName);
      }
    }
    
    // Retirer l'état de completion de l'étape si plus aucun produit
    if (!this.selectedProducts.has(stepName)) {
      this.markStepAsIncomplete(stepName);
    } else {
      // Mettre à jour l'aperçu de l'étape
      const step = this.container.querySelector(`.configurateur-step[data-step="${this.getStepNumberFromName(stepName)}"]`);
      if (step) {
        const stepProducts = this.selectedProducts.get(stepName);
        const productsArray = Array.from(stepProducts.values());
        const firstProduct = productsArray[0];
        const count = productsArray.length;
        
        this.updateStepPreview(step, {
          title: count > 1 ? `${count} articles sélectionnés` : firstProduct.title,
          image: firstProduct.image
        });
      }
    }
    
    // Mettre à jour l'affichage
    this.updateSummary();
  }

  markStepAsCompleted(input) {
    const step = input.closest('.configurateur-step');
    if (step) {
      step.setAttribute('data-step-completed', 'true');
      step.removeAttribute('data-step-skipped');
      
      // Mettre à jour l'aperçu avec le produit sélectionné
      this.updateStepPreview(step, {
        title: input.dataset.productTitle,
        image: input.dataset.productImage
      });
    }
  }

  markStepAsSkipped(skipBtn) {
    const step = skipBtn.closest('.configurateur-step');
    if (step) {
      step.setAttribute('data-step-skipped', 'true');
      step.removeAttribute('data-step-completed');
      
      // Mettre à jour l'aperçu avec "Aucune"
      this.updateStepPreview(step, {
        title: 'Aucune',
        image: null
      });
    }
  }

  markStepAsIncomplete(stepName) {
    // Trouver l'étape correspondante en fonction du nom
    const stepNumber = this.getStepNumberFromName(stepName);
    const step = this.container.querySelector(`.configurateur-step[data-step="${stepNumber}"]`);
    if (step) {
      step.removeAttribute('data-step-completed');
      step.removeAttribute('data-step-skipped');
      
      // Réinitialiser l'aperçu
      this.resetStepPreview(step);
    }
  }

  getStepNumberFromName(stepName) {
    const stepMapping = {
      'etape_1_toile': '1',
      'etape_2_acces': '2',
      'etape_3_pompe': '3',
      'etape_4_filtre': '4',
      'etape_5_chauffage': '5',
      'etape_6_eclairage': '6',
      'etape_7_toile_solaire': '7',
      'etape_8_enrouleur': '8',
      'etape_9_custom': '9'
    };
    return stepMapping[stepName] || null;
  }

  updateStepPreview(step, data) {
    const previewImage = step.querySelector('[data-preview-image]');
    const previewText = step.querySelector('[data-preview-text]');
    
    if (previewText) {
      previewText.textContent = data.title || 'Aucune';
    }
    
    if (previewImage) {
      if (data.image) {
        previewImage.innerHTML = `<img src="${data.image}" alt="${data.title}" width="32" height="32">`;
        previewImage.style.display = 'block';
      } else {
        previewImage.innerHTML = '';
        previewImage.style.display = 'none';
      }
    }
  }

  resetStepPreview(step) {
    const previewImage = step.querySelector('[data-preview-image]');
    const previewText = step.querySelector('[data-preview-text]');
    
    if (previewText) {
      previewText.textContent = 'Aucune';
    }
    
    if (previewImage) {
      previewImage.innerHTML = '';
      previewImage.style.display = 'none';
    }
  }

  async addConfigurationToCart() {
    // Ajouter un seul item (dépôt si défini), avec les choix dans les line properties
    
    if (!this.addToCartBtn) {
      console.error('Bouton d\'ajout au panier non trouvé');
      return;
    }
    
    this.addToCartBtn.disabled = true;
    this.addToCartBtn.innerHTML = '<span class="loading-spinner"></span> Ajout en cours...';
    
    try {
      const variantIdToAdd = this.depotVariantId ? parseInt(this.depotVariantId, 10) : parseInt(this.mainProduct.variantId, 10);

      if (!variantIdToAdd) {
        console.error('Aucun variant valide trouvé pour ajout panier');
        this.showNotification(this.i18n.genericError, 'error');
        return;
      }

      console.log('🏊 Produit principal:', this.mainProduct);
      console.log('🛒 Produits sélectionnés avant conversion:', Array.from(this.selectedProducts.entries()));

      const properties = {
        'Produit principal': `${this.mainProduct.title} (SKU: ${this.mainProduct.sku || 'N/A'})`
      };

      const stepLabels = {
        etape_1_toile: 'Étape 1 - Toile',
        etape_2_acces: 'Étape 2 - Accès à la piscine',
        etape_3_pompe: 'Étape 3 - Pompe',
        etape_4_filtre: 'Étape 4 - Filtre',
        etape_5_chauffage: 'Étape 5 - Chauffage',
        etape_6_eclairage: 'Étape 6 - Éclairage',
        etape_7_toile_solaire: 'Étape 7 - Toile solaire',
        etape_8_enrouleur: 'Étape 8 - Enrouleur de toile solaire',
        etape_9_custom: 'Étape 9 - Options supplémentaires'
      };

      this.selectedProducts.forEach((stepProducts, stepName) => {
        const stepLabel = stepLabels[stepName] || stepName;
        let index = 1;

        stepProducts.forEach((product, variantId) => {
          const propertyKey = stepProducts.size > 1 ? `${stepLabel} ${index}` : stepLabel;
          properties[propertyKey] = `${product.title} (SKU: ${product.sku || 'N/A'})`;
          index += 1;
        });
      });

      const items = [{
        id: variantIdToAdd,
        quantity: 1,
        properties
      }];

      this.selectedProducts.forEach((stepProducts, stepName) => {
        stepProducts.forEach((product, variantId) => {
          const variantIdInt = parseInt(variantId);
          console.log(`🔄 Conversion produit: ${product.title} - ID: ${variantId} -> ${variantIdInt}`);
        });
      });
      
      console.log('🚀 Tentative d\'ajout au panier avec items:', items);
      console.log('📋 Payload complet:', JSON.stringify({ items }, null, 2));
      
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ items })
      });
      
      console.log('📡 Réponse HTTP:', {
        status: response.status,
        statusText: response.statusText,
        headers: Object.fromEntries(response.headers.entries())
      });
      
      if (!response.ok) {
        const errorData = await response.text();
        console.error('❌ Erreur API Shopify:', {
          status: response.status,
          statusText: response.statusText,
          errorData: errorData,
          url: response.url
        });
        
        // Essayer de parser l'erreur JSON et déterminer le type d'erreur
        let errorMessage = this.i18n.genericError;
        try {
          const errorJson = JSON.parse(errorData);
          console.error('📄 Erreur JSON détaillée:', errorJson);
          
          const description = errorJson.description || errorJson.message || '';
          
          // Détecter le type d'erreur basé sur le message ou le status
          if (response.status === 422) {
            if (description.includes('plus en stock') || description.includes('out of stock')) {
              errorMessage = this.i18n.unavailable;
            } else if (
                description.includes('quantité maximale') || 
                description.includes('maximum quantity') ||
                description.includes('already in your cart') ||
                description.includes('déjà dans votre panier')
              ) {
              errorMessage = this.i18n.maxQuantity;
            } else if (
                description.includes('unavailable') ||
                description.includes('indisponible') ||
                description.includes('not available') ||
                description.includes('plus disponible')
              ) {
              errorMessage = this.i18n.unavailable;
            } else {
              errorMessage = this.i18n.unavailable;
            }
          } else if (response.status === 404) {
            errorMessage = this.i18n.unavailable;
          }
        } catch (e) {
          console.error('📄 Erreur texte brute:', errorData);
        }
        
        // Afficher le toast d'erreur
        this.showNotification(errorMessage, 'error');
        return; // Sortir sans réinitialiser le formulaire
      }
      
      const data = await response.json();
      console.log('✅ Réponse du panier réussie:', data);

      await this.setConfigurateurOrderCartAttribute();
      
      // Mettre à jour l'affichage du panier si disponible
      if (window.theme?.cart?.refresh) {
        window.theme.cart.refresh();
      }
      
      // Déclencher l'événement personnalisé pour les autres scripts
      document.dispatchEvent(new CustomEvent('cart:updated', { 
        detail: { items: data.items } 
      }));
      
      // Afficher le toast de succès
      this.showNotification(this.i18n.success, 'success');
      
      // Réinitialiser le formulaire après ajout réussi
      setTimeout(() => {
        this.resetForm();
      }, 1000);
      
    } catch (error) {
      console.error('Erreur lors de l\'ajout au panier:', error);
      
      // Vérifier si c'est une erreur réseau
      if (error.name === 'TypeError' && error.message.includes('fetch')) {
        this.showNotification(this.i18n.networkError, 'error');
      } else {
        this.showNotification(this.i18n.genericError, 'error');
      }
    } finally {
      if (this.addToCartBtn) {
        this.addToCartBtn.disabled = false;
        this.addToCartBtn.innerHTML = 'Ajouter la configuration au panier';
      }
    }
  }

  resetFormCompletely() {
    console.log('🔄 Réinitialisation complète du formulaire');
    
    // Décocher toutes les options (checkbox et radio)
    this.form.querySelectorAll('input[type="checkbox"]:checked, input[type="radio"]:checked').forEach(input => {
      input.checked = false;
    });
    
    // Vider la map des produits sélectionnés
    this.selectedProducts.clear();
    
    // Fermer toutes les étapes sauf la première et réinitialiser les états
    this.container.querySelectorAll('.configurateur-step').forEach((step, index) => {
      step.setAttribute('data-step-active', index === 0 ? 'true' : 'false');
      step.removeAttribute('data-step-completed');
      step.removeAttribute('data-step-skipped');
      
      // Réinitialiser l'aperçu
      this.resetStepPreview(step);
    });
    
    // Mettre à jour l'affichage
    this.updateSummary();
  }

  resetForm() {
    this.resetFormCompletely();
  }

  showNotification(message, type = 'info') {
    // Utiliser le système global de toast
    if (window.toast) {
      window.toast.show(message, { type });
    } else {
      console.log(`[${type}] ${message}`);
    }
  }

  async setConfigurateurOrderCartAttribute() {
    try {
      const response = await fetch('/cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          attributes: {
            'type de commande': 'Configurateur de piscine'
          }
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.warn('⚠️ Impossible de définir l\'attribut panier "type de commande" pour le configurateur:', errorText);
      }
    } catch (error) {
      console.warn('⚠️ Erreur lors de la mise à jour des attributs panier du configurateur:', error);
    }
  }
}

// Initialisation automatique
document.addEventListener('DOMContentLoaded', function() {
  const configurateurs = document.querySelectorAll('[data-configurateur-piscine]');
  configurateurs.forEach(configurateur => {
    new ConfigurateurPiscine(configurateur);
  });
});

// Export pour utilisation externe si nécessaire
window.ConfigurateurPiscine = ConfigurateurPiscine;